import {sendAnalyticsEvent} from '#/analytics/analytics';
import {
  HomeSection,
  HomeSectionHorizontalScroll,
  HomeSectionHorizontalScrollContent,
  HomeSectionSubtitle,
  HomeSectionTitle,
} from '#/components/home-content/home-section';
import {LandTile} from '#/components/land/land-tile';
import {useLandMarketInfos} from '#/hooks/query/use-land-market-info';
import {useLands} from '#/hooks/query/use-lands';
import {useCoinDisplayPreferences} from '#/hooks/use-coin-display-preferences';
import {useQueryNavigationActions} from '#/hooks/use-query-navigation-actions';
import {useSelectedLand} from '#/hooks/use-selected-land';
import {ClassName} from '#/utils/utils';
import {InjectedHomeSection, InjectedHomeSections, Land, LandMarketInfo} from '@shareland/shareland-common';
import {FunctionComponent} from 'react';

export const InjectedHomeSectionGroup: FunctionComponent<
  {
    title: string;
    subtitle: string;
    homeSection: InjectedHomeSection;
  } & ClassName
> = ({className, title, subtitle, homeSection}) => {
  const {
    actionMenu: {openInformation},
  } = useQueryNavigationActions();

  const {data: selectedLand} = useSelectedLand();
  const displayPref = useCoinDisplayPreferences();

  const {data: allLands = []} = useLands();
  const targetLands = homeSection.landTokenSymbols
    .map(tokenSymbol => allLands.find(land => land.tokenSymbol === tokenSymbol))
    .filter((land): land is Land => !!land);

  const {data: landMarketInfos = []} = useLandMarketInfos(targetLands);
  const landAndMarketInfos = targetLands.map((land, index) => ({
    land,
    landMarketInfo: landMarketInfos[index],
  }));

  const onLandClick = (land: Land, landMarketInfo: LandMarketInfo | undefined) => {
    openInformation(land);
    sendAnalyticsEvent.viewProperty({
      land,
      landMarketInfo,
    });
  };

  return (
    <HomeSection className={className}>
      <HomeSectionTitle>{title}</HomeSectionTitle>
      <HomeSectionSubtitle>{subtitle}</HomeSectionSubtitle>
      <HomeSectionHorizontalScroll className='snap-x snap-mandatory @xl/transaction-section:snap-none'>
        <HomeSectionHorizontalScrollContent className='grid grid-rows-injectedHomeSectionLandGrid grid-flow-col-dense auto-cols-injectedHomeSectionLandGrid gap-5'>
          {landAndMarketInfos.map(({land, landMarketInfo}, index) => (
            <LandTile
              key={`${homeSection.id}-land-tile-${index}`}
              selected={!!selectedLand && selectedLand.id === land?.id}
              land={land}
              landMarketInfo={landMarketInfo}
              onLand={onLandClick}
              displayPref={displayPref}
              bottomInformationVariant={getBottomInformationVariantFromHomeSectionId(
                homeSection.id as keyof InjectedHomeSections,
              )}
            />
          ))}
          <div className='w-0 h-4 px-1'></div>
        </HomeSectionHorizontalScrollContent>
      </HomeSectionHorizontalScroll>
    </HomeSection>
  );
};

const getBottomInformationVariantFromHomeSectionId = (homeSectionId: keyof InjectedHomeSections) => {
  switch (homeSectionId) {
    case 'premium':
    case 'discounted':
      return 'premium';
    case 'rentalYield':
      return 'rentalYield';
    default:
      return 'volume';
  }
};
